import React, { useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import './App.css';
import OrbitFieldAuto from './OrbitFieldAuto';
import release from './release.json';

/**
 * waiting4.xyz in its released state — the launch pack's site system:
 * Source Serif 4 headlines, IBM Plex Mono for labels and numerals, hairline
 * rules on every side, §-numbered sections, FIG. n captions. Anything
 * depicting the product itself (the screenshots, the orbit field) stays in the
 * app's own design.md system. Two backgrounds, one accent, no gradients.
 *
 * Motion layer: quiet scroll reveals (framer-motion, once, disabled under
 * prefers-reduced-motion) and §01 — the 17-second product film, exported
 * frame-exactly from the launch pack's scene engine. Ambient: muted, looped,
 * paused offscreen; under reduced motion it waits with controls instead.
 */
const APP_STORE_URL = 'https://apps.apple.com/app/id6795920773';

function Reveal({ children, delay = 0 }) {
  const reduce = useReducedMotion();
  if (reduce) return <>{children}</>;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -80px 0px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

function Film() {
  const reduce = useReducedMotion();
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v || reduce) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.3 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, [reduce]);

  return (
    <figure className="fig">
      {/* No speech, no sound — the film is silent by design, so no captions track. */}
      <video
        ref={ref}
        className="film"
        poster="/media/waiting4-film-poster.jpg"
        muted
        loop
        playsInline
        preload="metadata"
        autoPlay={!reduce}
        controls={reduce}
        aria-label="A silent 17-second tour of Waiting 4: the list, adding an item, the orbit view, the flyby tour, and privacy"
      >
        <source src="/media/waiting4-film-1080.webm" type="video/webm" />
        <source src="/media/waiting4-film-1080.mp4" type="video/mp4" />
      </video>
      <figcaption className="mono cap cap-dark">
        FIG. 0 — THE WHOLE APP, 17 SECONDS, SILENT
      </figcaption>
    </figure>
  );
}

function AppleMark({ fill }) {
  return (
    <svg width="15" height="18" viewBox="0 0 17 20" fill={fill} aria-hidden="true">
      <path d="M14.1 10.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.7.8-3.4.8-.7 0-1.8-.8-3-.8C4.8 5.3 3 6.4 2 8.2c-1.2 2.1-.3 5.2.9 6.9.6.8 1.3 1.8 2.3 1.8.9 0 1.3-.6 2.4-.6 1.1 0 1.4.6 2.4.6 1 0 1.6-.9 2.2-1.8.7-1 1-2 1-2.1-.1 0-2.1-.8-2.1-3.4M11.6 3.6c.5-.7.9-1.6.8-2.6-.8 0-1.8.6-2.4 1.3-.5.6-1 1.6-.8 2.5.9.1 1.9-.5 2.4-1.2" />
    </svg>
  );
}

function Logomark({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <circle
        cx="50"
        cy="50"
        r="36"
        fill="none"
        stroke="#C2410C"
        strokeWidth="13"
        strokeLinecap="round"
        strokeDasharray="180 46"
        strokeDashoffset="-136"
        transform="rotate(-90 50 50)"
      />
      <circle cx="50" cy="14" r="6.5" fill="#C2410C" />
    </svg>
  );
}

function Figure({ src, alt, caption, dark }) {
  return (
    <figure className="fig">
      <img src={src} alt={alt} className={dark ? 'shot shot-dark' : 'shot'} />
      <figcaption className={dark ? 'mono cap cap-dark' : 'mono cap'}>{caption}</figcaption>
    </figure>
  );
}

function StoreButton({ variant }) {
  const onDark = variant === 'accent';
  return (
    <a
      href={APP_STORE_URL}
      className={`mono store-btn ${onDark ? 'store-btn-accent' : ''}`}
      aria-label="Download Waiting 4 on the App Store"
    >
      <AppleMark fill={onDark ? '#FFFFFF' : '#F7F5F2'} />
      DOWNLOAD ON THE APP STORE
    </a>
  );
}

export default function App() {
  return (
    <div className="frame">
      <header className="masthead">
        <div className="brand">
          <Logomark />
          <span className="mono brandname">waiting4.xyz</span>
        </div>
        <a href="#get" className="mono rule-link">
          download →
        </a>
      </header>

      <main>
        <div className="mono issueline">
          <span>NO. 001 — THE OPEN LOOP</span>
          <span className="accent">OUT NOW — VERSION {release.version}</span>
        </div>

        <section className="hero">
          <div className="hero-text">
            <Reveal>
              <h1 className="serif display">You’re not crazy. It has been three weeks.</h1>
              <p className="lede">
                Waiting 4 tracks the things other people owe you — the reply, the refund, the “next
                week” — and quietly tells you how long it’s been.
              </p>
              <div className="cta" id="get">
                <div className="cta-row">
                  <StoreButton />
                  <a href="#film" className="mono rule-link">
                    watch the film →
                  </a>
                  <a href="#s03" className="mono rule-link">
                    see it drift →
                  </a>
                </div>
                <p className="mono fineprint">FREE · iPHONE, iOS 17+ · NO ACCOUNT · NO SETUP</p>
              </div>
            </Reveal>
          </div>
          <div className="hero-fig">
            <Reveal delay={0.12}>
              <Figure
                src="/store/iphone-6.9/01-hook.png"
                alt="The Waiting 4 list: four open loops, the longest wait at 23 days on top"
                caption="FIG. 1 — THE LIST, LONGEST WAIT FIRST"
              />
            </Reveal>
          </div>
        </section>

        <section className="stats">
          <div className="stat">
            <div className="mono statnum">10s</div>
            <div className="statlabel">to add a loop</div>
          </div>
          <div className="stat">
            <div className="mono statnum">0</div>
            <div className="statlabel">accounts, servers, sign-ups</div>
          </div>
          <div className="stat">
            <div className="mono statnum accent">9–21</div>
            <div className="statlabel">quiet hours for nudges</div>
          </div>
        </section>

        <section className="chapter dark" id="film">
          <div className="rail">
            <span className="mono railtext">§ 01 — THE FILM</span>
          </div>
          <div className="film-cell">
            <Reveal>
              <Film />
            </Reveal>
          </div>
        </section>

        <section className="chapter">
          <div className="rail">
            <span className="mono railtext">§ 02 — ONE LIST</span>
          </div>
          <div className="split split-text-first">
            <div className="prose">
              <Reveal>
                <h2 className="serif heading">Every open loop, longest wait first.</h2>
                <p className="body">
                  No projects, no folders, no priorities to set. The number of days is the biggest
                  thing on the screen, because that’s the thing you actually forget. Add one in
                  about ten seconds — who, what, and when to nudge you.
                </p>
              </Reveal>
            </div>
            <div className="figcell">
              <Reveal delay={0.1}>
                <Figure
                  src="/store/iphone-6.9/05-add.png"
                  alt="Adding an item: who, what, since when, and how often to nudge"
                  caption="FIG. 2 — TEN SECONDS TO ADD"
                />
              </Reveal>
            </div>
          </div>
        </section>

        <section className="chapter" id="s03">
          <div className="rail">
            <span className="mono railtext">§ 03 — ZOOM OUT</span>
          </div>
          <div className="split split-fig-first">
            <div className="figcell figcell-dark">
              <OrbitFieldAuto height={520} />
              <p className="mono cap cap-dark">FIG. 3 — DISTANCE IS TIME (LIVE)</p>
            </div>
            <div className="prose">
              <Reveal>
                <h2 className="serif heading">Watch them drift.</h2>
                <p className="body">
                  Pinch out on the list and it becomes a field — every loop in orbit around you,
                  distance set by how long it’s been. It’s the same list, just far enough back to
                  see the shape of it. Nothing here counts anything.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="chapter dark">
          <div className="rail">
            <span className="mono railtext">§ 04 — FLY OUT</span>
          </div>
          <div className="split split-text-first">
            <div className="prose">
              <Reveal>
                <h2 className="serif heading">Meet what you forgot.</h2>
                <p className="body">
                  A guided tour, one stop per loop. The camera flies out from you and calls out what
                  it passes on the way — the same list, told as a story instead of read as a table.
                </p>
              </Reveal>
            </div>
            <div className="figcell">
              <Reveal delay={0.1}>
                <Figure
                  dark
                  src="/store/iphone-6.9/03-tour.png"
                  alt="The flyby tour, departing from you toward an open loop"
                  caption="FIG. 4 — THE GUIDED TOUR"
                />
              </Reveal>
            </div>
          </div>
        </section>

        <section className="chapter">
          <div className="rail">
            <span className="mono railtext">§ 05 — PRIVACY</span>
          </div>
          <div className="split split-fig-first">
            <div className="figcell">
              <Reveal delay={0.1}>
                <Figure
                  src="/store/iphone-6.9/06-privacy.png"
                  alt="Nothing leaves your device"
                  caption="FIG. 5 — NOTHING TO LOG INTO"
                />
              </Reveal>
            </div>
            <div className="prose">
              <Reveal>
                <h2 className="serif heading">Nothing leaves your device.</h2>
                <p className="body">
                  No account. No sign-up. No server. No analytics. Your list is on your phone and
                  nowhere else, which is why there’s nothing to log into and nothing to delete.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="closing">
          <Reveal>
            <h2 className="serif closingline">One list. One button. Zero setup.</h2>
            <div className="closing-cta">
              <StoreButton variant="accent" />
            </div>
            <p className="mono fineprint">FREE · iPHONE, iOS 17+ · VERSION {release.version}</p>
          </Reveal>
        </section>
      </main>

      <footer className="footer">
        <span className="mono">© {new Date().getFullYear()} WAITING 4</span>
        <nav className="footer-links">
          <a href={APP_STORE_URL} className="mono">
            APP STORE
          </a>
          <a href="/changelog" className="mono">
            CHANGELOG
          </a>
          <a href="/support" className="mono">
            SUPPORT
          </a>
          <a href="/privacy" className="mono">
            PRIVACY
          </a>
        </nav>
      </footer>
    </div>
  );
}
